import { PureComponent } from 'react';
import PropTypes from 'prop-types';

import {
  AddToCalendarButton,
  SectionItem,
  DateTimeDetails,
  envConfigSelectors,
} from '~/components';
import { formatPrice } from '~/utils';
import { BookingHeader } from './booking-header';
import { AppointmentBookingHeader } from './appointment-booking-header';
import { BookingDate } from './booking-date';
import { BookingLocation, BookingAddress } from './booking-location';
import { BookingPartner as CommonBookingPartner } from './booking-partner';
import { BookingContext, bookingView } from './booking.context';

import { getEstimationTimeForAllProducts } from '~/ocab-services/measure/utils';
import styles from './BookingReview.scss';

export class BookingReview extends PureComponent {
  constructor(props) {
    super(props);

    if (!window.sessioncamConfiguration) {
      window.sessioncamConfiguration = {};
    }
    window.sessioncamConfiguration.elementsToRemove = '.sessioncamhidemap';
  }

  render() {
    const {
      booking: {
        appointmentType,
        partner = {},
        price,
        slot,
        store,
        address: propertyAddress,
        products,
        id: bookingId,
        department = {},
        answers,
      },
      config: {
        partnerTitle,
        extraPartnerTitle,
        BookingReview: {
          BookingPartner: configBookingPartner,
          BookingHeader: configBookingHeader,
          BookingDate: configBookingDate = {},
          BookingAddress: configBookingAddress = {},
          PhoneSectionComponent = () => null,
          sectionTitle,
          shopDetails,
          YourAddress: { title: yourDetailsTitle } = {},
        } = {},
        ReviewForm = {},
        hidePartnerPrefix,
        hidePartnerNameUnderline,
      },
      serviceConfig,
      isRemoteV1,
      isRemoteV2,
      remote,
      hideShopDetails,
      hideShopAddress,
      hidePartnerDetails,
      hideAddToCalendarButton,
      hidePriceDetails,
      hidePhoneSection,
      formatBookingAddressFunc,
    } = this.props;

    const address = envConfigSelectors.selectUserAddress(
      serviceConfig,
      answers,
      appointmentType.id,
    );
    const BookingPartner = configBookingPartner || CommonBookingPartner;
    const appointmentEstimationDuration =
      configBookingDate?.buildAppointmentEstimationDurationDescription &&
      configBookingDate.buildAppointmentEstimationDurationDescription(
        getEstimationTimeForAllProducts(products),
      );
    const reviewDetails = (context) =>
      context === bookingView.appointment ? (
        <div
          className={`${styles.BookingReview__booking_info} ${styles.Appointment__booking_info}`}
        >
          <BookingDate slot={slot} {...configBookingDate} />
          {address ? (
            <BookingAddress address={address} />
          ) : (
            <BookingLocation shop={store} {...BookingReview} />
          )}
          <BookingPartner
            partnerName={partner.name}
            photoUrl={partner.photoUrl}
            shopName={store.name}
            partnerTitle={partnerTitle}
            extraPartnerTitle={extraPartnerTitle}
            hidePartnerPrefix={hidePartnerPrefix}
            hidePartnerNameUnderline={hidePartnerNameUnderline}
          />
        </div>
      ) : (
        <dl className={styles.BookingReview__review_details}>
          <SectionItem title="Appointment type" definition={appointmentType.name} />
          {propertyAddress && (
            <SectionItem
              title={configBookingAddress?.title ?? 'Property address'}
              definition={formatBookingAddressFunc(propertyAddress)}
            />
          )}
          <DateTimeDetails
            title="Date and time"
            selectedDateSlot={slot.date}
            selectedTimeSlot={slot.time}
            formatTimeSlot={configBookingDate.formatTimeSlot || ReviewForm.formatTimeSlot}
          >
            {appointmentEstimationDuration && <p>{appointmentEstimationDuration}</p>}
            {!hideAddToCalendarButton && !isRemoteV1 && <AddToCalendarButton />}
          </DateTimeDetails>
          {hidePartnerDetails ? null : (
            <SectionItem
              title={partnerTitle}
              definition={
                <BookingPartner
                  partnerName={partner.name}
                  photoUrl={partner.photoUrl}
                  shopName={store.name}
                  partnerTitle={partnerTitle}
                  extraPartnerTitle={extraPartnerTitle}
                  hidePartnerPrefix={hidePartnerPrefix}
                  hidePartnerNameUnderline={hidePartnerNameUnderline}
                />
              }
              dataTest="partner"
            />
          )}
          {address ? (
            <SectionItem
              title={yourDetailsTitle ?? 'Your address'}
              definition={
                <div>
                  <p>{address}</p>
                </div>
              }
            />
          ) : null}
          {hideShopDetails ? null : (
            <SectionItem
              title={
                isRemoteV1 || isRemoteV2
                  ? remote.ReviewForm.shopDetails.title
                  : shopDetails?.title || 'Shop'
              }
              definition={
                <div>
                  <p>{store.name}</p>
                  {!hideShopAddress && (
                    <p>{isRemoteV2 ? remote.shopAddress : store.address.addressLines}</p>
                  )}
                </div>
              }
            />
          )}
          {hidePhoneSection ? null : <PhoneSectionComponent store={store} />}
          <SectionItem title="Booking reference" definition={bookingId} />
          {!hidePriceDetails && <SectionItem title="Price" definition={formatPrice(price)} />}
        </dl>
      );

    return (
      <BookingContext.Consumer>
        {(context) => (
          <div
            className={`${styles.BookingReview} ${context === bookingView.appointment ? styles.AppointmentBordered : ''}`}
            data-testid="appointmentReview"
          >
            {context === bookingView.appointment ? (
              <AppointmentBookingHeader
                appointmentType={appointmentType}
                price={price}
                bookingId={bookingId}
                department={department}
                {...configBookingHeader}
              />
            ) : (
              <BookingHeader title={sectionTitle} />
            )}
            {reviewDetails(context)}
          </div>
        )}
      </BookingContext.Consumer>
    );
  }
}

BookingReview.propTypes = {
  booking: PropTypes.shape({}).isRequired,
  config: PropTypes.shape({}),
  remote: PropTypes.shape({}),
  serviceConfig: PropTypes.shape({
    id: PropTypes.string.isRequired,
    appointmentTypes: PropTypes.shape({}).isRequired,
    addressQuestionId: PropTypes.string,
    landingPageUrl: PropTypes.string,
  }).isRequired,
  isRemoteV1: PropTypes.bool,
  isRemoteV2: PropTypes.bool,
  hideShopDetails: PropTypes.bool.isRequired,
  hidePartnerDetails: PropTypes.bool.isRequired,
  hideAddToCalendarButton: PropTypes.bool.isRequired,
  hidePriceDetails: PropTypes.bool.isRequired,
  formatBookingAddressFunc: PropTypes.func.isRequired,
  hidePhoneSection: PropTypes.bool.isRequired,
};

BookingReview.defaultProps = {
  config: {},
  remote: {},
  isRemoteV1: false,
  isRemoteV2: false,
};
